// Semantische Suche fuer die TopBar. Holt sich Embeddings vom lokalen
// Embed-Proxy (/v2-api/ai/embed, bge-m3 im Inferenzdienst), cached sie in
// localStorage und rechnet Cosine-Similarity im Browser. Faellt
// transparent zurueck, wenn der Backend-Probe meldet dass der Inferenzdienst
// down ist -- dann rendert NavSearchPanel nur die Fuzzy-Treffer.
//
// Index-Eintraege haben einen `key` (z.B. "/zuhause/lipoguard" oder
// "task:wob-data-backup") und einen `text` (Label + Aliase). Der key
// bleibt im _embeddings-Map und ist die Lookup-Adresse aus dem
// Aufrufer-Code. Der text definiert das Embedding -- gleicher Text
// ergibt deterministisch dasselbe Embedding und triggert localStorage-
// Cache-Hits ueber alle Aufrufe hinweg.
//
// Architektur:
//   1. initSemantic([{key, text}]) -- beim Boot
//      a) Probe /v2-api/ai/info -> wenn nicht available, exit early.
//      b) Pro Entry: LocalStorage-Lookup (key: "emb:<model>:<text>").
//      c) Fehlende Texte als Batch an /v2-api/ai/embed.
//      d) Result-Embeddings in _embeddings + localStorage.
//   2. addEntities([{key, text}]) -- nach Init, fuer dynamische Daten
//      (Tasks, Automations, Hosts, Agents). Skippt was schon embedded
//      ist; embedded den Rest in einer Batch.
//   3. queryEmbedding(text) -- bei jedem Tastendruck (debounced extern)
//   4. cosine(a, b) -- im Caller.
//
// Persistenz: localStorage haelt alle Entity-Embeddings (je ~10 KB).
// Bei ~200 Eintraegen sind das ~2 MB; localStorage-Limit ist 5-10 MB
// pro Origin, also reichlich Headroom.

import { getJSON, postJSON } from "./api";
import {
  loadAllEmbeddings,
  migrateLegacyEmbeddings,
  purgeOtherModels,
  saveEmbedding,
} from "./embedding-store";

// Alt-Prefix aus der localStorage-Aera (v1): die Embed-Texte enthielten
// damals Live-Werte (Zigbee ON/OFF, Batterie-%), jede Wertaenderung
// erzeugte einen neuen Eintrag, und der Muell blieb liegen. Seit dem
// Umzug nach IndexedDB (embedding-store.ts) liegt hier gar nichts mehr;
// die Zeile raeumt nur noch die Reste weg.
const STALE_PREFIXES = ["emb:"];

function purgeStalePrefixes(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && STALE_PREFIXES.some((p) => k.startsWith(p))) stale.push(k);
    }
    for (const k of stale) localStorage.removeItem(k);
  } catch {
    // Storage disabled -- egal.
  }
}

// _diskCache spiegelt die IndexedDB-Ablage im Speicher: der Suchpfad
// schlaegt synchron nach (wie vorher bei localStorage), geschrieben wird
// im Hintergrund. Key ist "<modell>:<text>".
let _diskCache = new Map<string, Float32Array>();

let _model = $state<string | null>(null);
let _available = $state(false);
let _ready = $state(false);
let _embeddings = $state<Map<string, Float32Array>>(new Map());
// _inflight: Anzahl der laufenden Embed-Calls (Init-Batches + Querys).
// Liest die TopBar fuer den "AI rechnet"-Punkt. >0 = busy.
let _inflight = $state(0);
let _lastError = $state<string | null>(null);

const queryCache = new Map<string, Float32Array>();

// _attempted: Keys, die wir mindestens einmal in einer Embed-Batch
// verschickt haben (egal ob Erfolg oder Fehler). Ohne das wuerde der
// addEntities-$effect in NavSearchPanel bei JEDEM Live-Store-Tick
// (zigbee/docker/agents aktualisieren im Sekundentakt) erneut alle
// noch-nicht-embeddedn Keys schicken. Schlaegt eine Batch unter Last
// fehl, landen ihre Keys nie in _embeddings und wuerden endlos neu
// versucht -- das "Ollama triggert staendig, auch ohne Eingabe"-Symptom.
// Jeder Key wird hoechstens einmal versucht; nach einem Fehlschlag gibt
// eine Cooldown-Freigabe genau EINEN spaeteren Neuversuch frei (falls
// der Dienst zwischenzeitlich wiederkommt), kein Sturm.
const _attempted = new Set<string>();
const RETRY_COOLDOWN_MS = 60_000;

// _embedQueue serialisiert ALLE Embed-Calls. bge-m3 auf der CPU rechnet
// 2 Anfragen parallel (parallel = 2 in deploy/inferenz/modelle.ini; bis
// 21.10.2026 Ollama mit NUM_PARALLEL=2); zu viele in Flight fuehren zu
// Queue-Pileup + 10s-Timeout-Errors. Wir reichen Calls strikt
// nacheinander durch -- die ganze Init + Inventar-Indexing dauert dann
// 5-10s statt parallel zu rennen und alles zu verstopfen.
let _embedQueue: Promise<void> = Promise.resolve();
async function _enqueue<T>(work: () => Promise<T>): Promise<T> {
  const prev = _embedQueue;
  let resolve!: () => void;
  _embedQueue = new Promise<void>((r) => (resolve = r));
  await prev;
  try {
    return await work();
  } finally {
    resolve();
  }
}

// _queryAbort: laufenden Query-Embed abbrechen wenn schon der naechste
// Tastendruck reinkommt. Verhindert dass der Dienst veraltete Queries
// abarbeitet die niemand mehr will.
let _queryAbort: AbortController | null = null;

type EmbedResponse = {
  model: string;
  embedding?: number[];
  embeddings?: number[][];
};

type AIInfo = {
  available: boolean;
  model: string;
};

export function isAvailable(): boolean {
  return _available;
}

export function isReady(): boolean {
  return _ready;
}

export function isBusy(): boolean {
  return _inflight > 0;
}

export function lastError(): string | null {
  return _lastError;
}

export function model(): string | null {
  return _model;
}

export function getEmbedding(key: string): Float32Array | undefined {
  return _embeddings.get(key);
}

export interface IndexEntry {
  key: string;
  text: string;
}

// initSemantic: einmal pro Session aufrufen. Probt /v2-api/ai/info,
// embedded die Initial-Entries. Spaetere Daten (Tasks, Hosts, ...)
// gehen ueber addEntities.
export async function initSemantic(entries: IndexEntry[]): Promise<void> {
  // Alt-Eintraege des v1-Prefix wegwerfen (einmal pro Session, billig).
  purgeStalePrefixes();
  // 1) Probe
  try {
    const info = await getJSON<AIInfo>("/v2-api/ai/info");
    if (!info.available) {
      _available = false;
      _ready = true;
      return;
    }
    _model = info.model;
    _available = true;
  } catch {
    _available = false;
    _ready = true;
    return;
  }

  // 1b) Ablage laden, BEVOR embedded wird -- sonst haelt der Cache
  // jeden Eintrag fuer neu und schickt ihn los. Was noch im alten
  // localStorage liegt, wird uebernommen statt neu gerechnet.
  _diskCache = await loadAllEmbeddings();
  for (const [k, v] of migrateLegacyEmbeddings()) _diskCache.set(k, v);
  // Vektoren eines frueheren Modells sind wertlos -- im Hintergrund weg.
  void purgeOtherModels(_model ?? "");

  // 2) Embed der Initial-Entries.
  await _embedAndStore(entries);
  _ready = true;
}

// addEntities: fuer dynamische Daten, die erst nach dem Auth-Boot ueber
// die Summary-Stores reinkommen (Tasks, Automations, Hosts, Agents).
// Skippt bereits-embedded keys, embed den Rest in einer Batch. Idempotent.
export async function addEntities(entries: IndexEntry[]): Promise<void> {
  if (!_available) return;
  // Bereits embedded ODER schon versucht -> ueberspringen. Das entkoppelt
  // die Aufruf-Frequenz (Live-Store-Ticks) von der Embed-Frequenz.
  const fresh = entries.filter(
    (e) => e.key && e.text && !_embeddings.has(e.key) && !_attempted.has(e.key),
  );
  if (fresh.length === 0) return;
  for (const e of fresh) _attempted.add(e.key);
  const failed = await _embedAndStore(fresh);
  // Fehlgeschlagene Keys nach einer Cooldown-Phase wieder freigeben, damit
  // sie EINMAL neu versucht werden (statt bei jedem Tick) -- z.B. wenn
  // der Dienst nach kurzer Ueberlast wieder antwortet.
  if (failed.length > 0) {
    setTimeout(() => {
      for (const k of failed) _attempted.delete(k);
    }, RETRY_COOLDOWN_MS);
  }
}

// _embedAndStore: gemeinsame Logik von initSemantic + addEntities.
// LocalStorage-Lookup, Misses batchen, _embeddings mergen. Rueckgabe =
// die Keys, deren Embed-Batch fehlschlug (fuer den Cooldown-Retry in
// addEntities); leer bei vollem Erfolg / reinen Cache-Hits.
async function _embedAndStore(entries: IndexEntry[]): Promise<string[]> {
  if (entries.length === 0) return [];
  const cacheKey = (text: string) => `${_model}:${text}`;
  // Was DIESER Aufruf beitraegt. Geschrieben wird immer in den Bestand,
  // wie er im Augenblick des Schreibens ist (uebernimm) -- nicht in eine
  // Kopie vom Anfang. Bis 04.10.2026 arbeitete jeder Aufruf mit seiner
  // eigenen Kopie und setzte sie am Ende als Ganzes: liefen zwei
  // gleichzeitig (Inventar und Einstellungen nach dem Boot), warf der
  // spaeter fertige die Vektoren des anderen wieder hinaus. Und weil die
  // Schluessel als "versucht" galten, kamen sie bis zum Neuladen nicht
  // wieder.
  const neu = new Map<string, Float32Array>();
  const uebernimm = (): void => {
    if (neu.size === 0) return;
    const merged = new Map(_embeddings);
    for (const [k, v] of neu) merged.set(k, v);
    _embeddings = merged;
    neu.clear();
  };
  const toFetch: IndexEntry[] = [];
  for (const e of entries) {
    if (!e.text) continue;
    const cached = loadCached(cacheKey(e.text));
    if (cached) {
      neu.set(e.key, cached);
    } else {
      toFetch.push(e);
    }
  }
  // Treffer aus der Ablage sofort sichtbar machen, nicht erst nach dem
  // letzten Batch.
  uebernimm();
  const BATCH = 16;
  for (let i = 0; i < toFetch.length; i += BATCH) {
    const slice = toFetch.slice(i, i + BATCH);
    const ok = await _enqueue(async () => {
      _inflight++;
      try {
        const resp = await postJSON<EmbedResponse>("/v2-api/ai/embed", {
          texts: slice.map((s) => s.text),
        });
        if (!resp.embeddings || resp.embeddings.length !== slice.length) {
          console.warn("ai embed batch: dim mismatch", resp);
          return true;
        }
        for (let j = 0; j < slice.length; j++) {
          const emb = new Float32Array(resp.embeddings[j]);
          neu.set(slice[j].key, emb);
          saveCached(cacheKey(slice[j].text), emb);
        }
        return true;
      } catch (err) {
        console.warn("ai embed batch failed", err);
        _lastError = String(err);
        return false;
      } finally {
        _inflight--;
      }
    });
    uebernimm();
    if (!ok) {
      // Diese + alle restlichen (nicht mehr versuchten) Misses als
      // fehlgeschlagen melden -- der Caller gibt sie fuer den Retry frei.
      return toFetch.slice(i).map((e) => e.key);
    }
  }
  return [];
}

// queryEmbedding: einzelner Such-Query. In-memory-Cache + AbortController
// fuer den Fall dass der User schneller tippt als das Embedding zurueck-
// kommt -- veraltete Calls werden abgebrochen damit der Dienst nicht
// in einem Queue-Pileup steckt. Calls laufen ueber _enqueue, d.h. nicht
// parallel zu Init/AddEntities.
export async function queryEmbedding(text: string): Promise<Float32Array | null> {
  if (!_available || !text) return null;
  const cached = queryCache.get(text);
  if (cached) return cached;
  // Vorhergehenden Query abbrechen (falls noch laufend).
  if (_queryAbort) _queryAbort.abort();
  const ctrl = new AbortController();
  _queryAbort = ctrl;
  return _enqueue(async () => {
    if (ctrl.signal.aborted) return null;
    _inflight++;
    try {
      const resp = await postJSON<EmbedResponse>(
        "/v2-api/ai/embed",
        { text },
        ctrl.signal,
      );
      if (!resp.embedding) return null;
      const emb = new Float32Array(resp.embedding);
      if (queryCache.size >= 64) {
        const first = queryCache.keys().next().value;
        if (first !== undefined) queryCache.delete(first);
      }
      queryCache.set(text, emb);
      return emb;
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return null;
      console.warn("ai query embed failed", err);
      _lastError = String(err);
      return null;
    } finally {
      _inflight--;
      if (_queryAbort === ctrl) _queryAbort = null;
    }
  });
}

// Cosine-Similarity. bge-m3 liefert L2-normalisierte Embeddings, d. h.
// Dot-Product == Cosine. Wir bleiben trotzdem defensiv mit Division
// durch die Normen, falls jemand das Modell tauscht.
export function cosine(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  if (denom < 1e-9) return 0;
  return dot / denom;
}

// === localStorage Helper ====================================

function loadCached(key: string): Float32Array | null {
  return _diskCache.get(key) ?? null;
}

function saveCached(key: string, emb: Float32Array): void {
  _diskCache.set(key, emb);
  saveEmbedding(key, emb);
}
